import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { supabase } from '../../lib/supabase';

interface PageContent {
  [key: string]: string;
}

export default function Contact() {
  const { t } = useTranslation();
  const [content, setContent] = useState<PageContent>({});
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    loadPageContent();
  }, []);

  const loadPageContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('page_content')
        .select('*')
        .eq('page_name', 'contact');

      if (error) throw error;

      const contentMap: PageContent = {};
      data?.forEach((item) => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error loading page content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');

    try {
      const { error } = await supabase
        .from('contact_messages')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            subject: formData.subject,
            message: formData.message,
            status: 'new',
            created_at: new Date().toISOString()
          }
        ]);

      if (error) throw error;

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <i className="ri-loader-4-line text-4xl text-[#FCD34D] animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/8ea94fe9dd855927baf3830865598078.jpg"
            alt="Contact Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
            {content.hero_title || 'İletişim'}
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            {content.hero_subtitle || 'Herhangi bir sorunuz için ekibimizle iletişime geçin'}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-[#1F1F1F] p-8 rounded-lg">
              <h2 className="text-3xl font-bold text-white mb-6">İletişim Formu</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                    Adınız Soyadınız
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] text-white text-sm rounded-lg border border-gray-700 focus:border-[#FCD34D] focus:outline-none"
                    placeholder="Adınızı ve soyadınızı girin"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                    E-posta Adresiniz
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] text-white text-sm rounded-lg border border-gray-700 focus:border-[#FCD34D] focus:outline-none"
                    placeholder="E-posta adresinizi girin"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">
                    Telefon Numaranız
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0A0A0A] text-white text-sm rounded-lg border border-gray-700 focus:border-[#FCD34D] focus:outline-none"
                    placeholder="Telefon numaranızı girin"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-400 mb-2">
                    Konu
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#0A0A0A] text-white text-sm rounded-lg border border-gray-700 focus:border-[#FCD34D] focus:outline-none"
                  >
                    <option value="">Konu seçin</option>
                    <option value="general">Genel Bilgi</option>
                    <option value="service">Hizmet Hakkında</option>
                    <option value="quote">Fiyat Teklifi</option>
                    <option value="feedback">Geri Bildirim</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                    Mesajınız
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-[#0A0A0A] text-white text-sm rounded-lg border border-gray-700 focus:border-[#FCD34D] focus:outline-none resize-none"
                    placeholder="Mesajınızı buraya yazın"
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="whitespace-nowrap w-full px-8 py-4 bg-[#FCD34D] text-black font-semibold rounded-lg hover:bg-[#fcd34ddd] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-500/10 border border-green-500 rounded-lg text-green-500 text-sm">
                    Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-sm">
                    Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-[#1F1F1F] p-8 rounded-lg mb-8">
                <h2 className="text-3xl font-bold text-white mb-6">İletişim Bilgileri</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-[#FCD34D]"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Adres</h3>
                      <p className="text-gray-400 whitespace-pre-line">
                        {content.address || '123 Oto Caddesi, Merkez\nİstanbul, Türkiye 34000'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-[#FCD34D]"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Telefon</h3>
                      <p className="text-gray-400">
                        {content.phone || '+90 (555) 123-4567'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-[#FCD34D]"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">E-posta</h3>
                      <p className="text-gray-400">
                        {content.email || 'info@autodetailing.com'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-2xl text-[#FCD34D]"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Çalışma Saatleri</h3>
                      <p className="text-gray-400 whitespace-pre-line">
                        {content.working_hours || 'Pazartesi - Cuma: 08:00 - 18:00\nCumartesi: 09:00 - 16:00\nPazar: Kapalı'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Bizi Takip Edin</h3>
                  <div className="flex gap-3">
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg hover:bg-[#FCD34D]/20 transition-colors cursor-pointer">
                      <i className="ri-facebook-fill text-xl text-[#FCD34D]"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg hover:bg-[#FCD34D]/20 transition-colors cursor-pointer">
                      <i className="ri-instagram-line text-xl text-[#FCD34D]"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg hover:bg-[#FCD34D]/20 transition-colors cursor-pointer">
                      <i className="ri-twitter-x-line text-xl text-[#FCD34D]"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center bg-[#FCD34D]/10 rounded-lg hover:bg-[#FCD34D]/20 transition-colors cursor-pointer">
                      <i className="ri-youtube-line text-xl text-[#FCD34D]"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-700">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d201.39408490146457!2d37.39476948697832!3d37.07948546350283!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1531e53472bbb173%3A0x35df3128bc3da3f1!2sMrt%20Soft!5e1!3m2!1str!2str!4v1736000000000!5m2!1str!2str"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="MRT Soft Gaziantep Konumu"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
